import {
  CalendarOutlined,
  CheckCircleFilled,
  CloseCircleFilled,
  DeleteOutlined,
  DownOutlined,
  DownloadOutlined,
  EditOutlined,
  FileTextOutlined,
  FilterOutlined,
  ReloadOutlined,
  RightOutlined,
  SearchOutlined,
  ShoppingOutlined,
  SyncOutlined,
  UndoOutlined,
  UserOutlined,
  WalletOutlined,
} from '@ant-design/icons';
import {
  Badge,
  Button,
  Card,
  Col,
  DatePicker,
  Divider,
  Input,
  Modal,
  Pagination,
  Popconfirm,
  Row,
  Select,
  Space,
  Spin,
  Table,
  Tag,
  Tooltip,
  Typography,
  message,
} from 'antd';
import dayjs from 'dayjs';
import PropTypes from 'prop-types';
import {useCallback, useEffect, useMemo, useState} from 'react';
import {useSelector} from 'react-redux';

import {StatCard} from '../../../../CommonComponents/Cards';
import {
  RQGetAllBillsAPI,
  RqDeleteBill,
  RqGetDeletedBills,
  RqPermanentDeleteBill,
  RqRestoreBill,
} from '../../../../reactQuery/apis';

import {BillDownload} from './DownloadBills';
import EditBill from './EditBill/EditBill';

const {Text, Title} = Typography;
const {Option}      = Select;
const {RangePicker} = DatePicker;

/* ── helpers ──────────────────────────────────────────────────────────────── */
const fmt    = iso => dayjs(iso).format('DD MMM YYYY');
const fmtCur = v   => `₹${Number(v ?? 0).toLocaleString('en-IN', {minimumFractionDigits: 2})}`;

const paymentStatusColor = {PAID: 'blue', PARTIAL: 'purple', PENDING: 'orange', FAILED: 'red'};
const billTypeColor      = {GST: 'cyan', NON_GST: 'geekblue'};
const billStatusColor    = {COMPLETED: 'green', CANCELLED: 'red', REFUNDED: 'volcano', HOLD: 'gold'};

const formatBillNumber = record => {
  // 1. Return the exact bill number stored in backend database when created
  if (record?.billNumber) {
    return record.billNumber;
  }
  if (record?.invoiceNo) {
    return record.invoiceNo;
  }
  if (record?.billNo) {
    return record.billNo;
  }

  // 2. Fallback only if no stored bill number exists
  const isGst = record?.billType === 'GST' || record?.isGst;
  const prefix = isGst ? 'GST-SI-B' : 'NGST-SI-B';

  const date = new Date(record?.createdAt || record?.date || Date.now());
  const year = date.getFullYear();
  const month = date.getMonth();

  const startYear = month >= 3 ? year : year - 1;
  const endYear = (startYear + 1).toString().slice(-2);
  const fy = `${startYear}-${endYear}`;
  const day = String(date.getDate()).padStart(2, '0');

  const seq = typeof record?.id === 'number' || /^\d+$/.test(record?.id)
    ? String(record.id).padStart(4, '0')
    : '0001';

  return `${prefix}-${fy}-${day}-${seq}`;
};

/* ── tiny sub-components ──────────────────────────────────────────────────── */
const AmountCell = ({bill}) => (
  <div>
    <Text strong className={'text-primary-900'}>{fmtCur(bill.finalAmount)}</Text>
    {Number(bill.pendingAmount) > 0 && (
      <div className={'text-red-500 text-xs font-semibold'}>
        Pending: {fmtCur(bill.pendingAmount)}
      </div>
    )}
    {Number(bill.discountAmount) > 0 && (
      <div className={'text-emerald-700 text-xs'}>−{fmtCur(bill.discountAmount)} off</div>
    )}
  </div>
);
AmountCell.propTypes = {bill: PropTypes.object.isRequired};

/* ── item expand table ────────────────────────────────────────────────────── */
const itemCols = [
  {title: 'Product',    dataIndex: 'productName', key: 'productName',
    render: v => <Text strong className={'text-blue-800'}>{v}</Text>},
  {title: 'HSN',        dataIndex: 'hsnCode',     key: 'hsnCode',    align: 'center',
    render: v => v ? <Tag className={'font-mono text-xs'}>{v}</Tag> : '—'},
  {title: 'Unit',       dataIndex: 'unit',         key: 'unit',        align: 'center',
    render: v => v ? <Tag color={'blue'}>{v}</Tag> : '—'},
  {title: 'Bags',       dataIndex: 'bags',         key: 'bags',        align: 'center',
    render: v => v ?? '—'},
  {title: 'Qty',        dataIndex: 'quantity',     key: 'quantity',    align: 'center',
    render: v => <Tag color={'geekblue'}>{v}</Tag>},
  {title: 'Unit Price', dataIndex: 'unitPrice',    key: 'unitPrice',   align: 'right',
    render: v => fmtCur(v)},
  {title: 'CGST',       dataIndex: 'cgst',         key: 'cgst',        align: 'center',
    render: v => v > 0 ? <Tag color={'purple'}>{v}%</Tag> : '—'},
  {title: 'SGST',       dataIndex: 'sgst',         key: 'sgst',        align: 'center',
    render: v => v > 0 ? <Tag color={'purple'}>{v}%</Tag> : '—'},
  {title: 'IGST',       dataIndex: 'igst',         key: 'igst',        align: 'center',
    render: v => v > 0 ? <Tag color={'magenta'}>{v}%</Tag> : '—'},
  {title: 'Line Total', dataIndex: 'totalPrice',   key: 'totalPrice',  align: 'right',
    render: v => <Text strong className={'text-blue-700'}>{fmtCur(v)}</Text>},
];

/* ── customer summary panel shown when a customer filter is active ─────────── */
const CustomerSummaryPanel = ({bills, customerName}) => {
  const summary = useMemo(() => {
    const gstBills    = bills.filter(b => b.billType === 'GST');
    const nonGstBills = bills.filter(b => b.billType === 'NON_GST');
    const pending     = bills.filter(b => b.paymentStatus === 'PENDING' || b.paymentStatus === 'PARTIAL');

    const totalSales      = bills.reduce((s, b) => s + (b.finalAmount   ?? 0), 0);
    const totalPending    = bills.reduce((s, b) => s + (b.pendingAmount  ?? 0), 0);
    const totalPaid       = bills.reduce((s, b) => s + (b.paidAmount     ?? 0), 0);
    const totalDiscount   = bills.reduce((s, b) => s + (b.discountAmount ?? 0), 0);

    /* aggregate products across all bills */
    const productMap = {};
    bills.forEach(bill => {
      (bill.items ?? []).forEach(item => {
        const key = item.productName;
        if (!key) {return;}
        if (!productMap[key]) {
          productMap[key] = {productName: key, unit: item.unit ?? '', totalQty: 0, totalBags: 0};
        }
        productMap[key].totalQty  += item.quantity ?? 0;
        productMap[key].totalBags += item.bags     ?? 0;
      });
    });
    const products = Object.values(productMap).sort((a, b) => b.totalQty - a.totalQty);

    return {gstBills, nonGstBills, pending, totalSales, totalPending, totalPaid, totalDiscount, products};
  }, [bills]);

  if (!bills.length) {return null;}

  return (
    <Card
      className={'rounded-2xl border-0 shadow-sm mb-4 overflow-hidden'}
      styles={{body: {padding: '16px 20px'}}}
    >
      {/* header */}
      <div className={'flex items-center gap-3 mb-4'}>
        <div
          className={'w-10 h-10 rounded-xl flex items-center justify-center text-white font-bold text-base shrink-0'}
          style={{background: 'linear-gradient(135deg, #3a5f98, #92a6c6)'}}
        >
          {customerName?.charAt(0)?.toUpperCase() ?? 'C'}
        </div>
        <div>
          <p className={'font-bold text-gray-800 m-0 text-base'}>{customerName}</p>
          <p className={'text-xs text-gray-400 m-0'}>{bills.length} bill{bills.length !== 1 ? 's' : ''} found</p>
        </div>
      </div>

      {/* stat cards */}
      <div className={'grid grid-cols-2 md:grid-cols-4 gap-3 mb-4'}>
        {[
          {label: 'Total Bills',    value: bills.length,               color: 'text-primary-700',  bg: 'bg-primary-50   border-primary-200'},
          {label: 'Total Sales',    value: fmtCur(summary.totalSales), color: 'text-emerald-700',  bg: 'bg-emerald-50   border-emerald-200'},
          {label: 'Total Paid',     value: fmtCur(summary.totalPaid),  color: 'text-blue-700',     bg: 'bg-blue-50      border-blue-200'},
          {label: 'Total Pending',  value: fmtCur(summary.totalPending), color: 'text-red-600',    bg: 'bg-red-50       border-red-200'},
        ].map(s => (
          <div key={s.label} className={`${s.bg} border rounded-xl px-4 py-3 text-center`}>
            <p className={'text-xs text-gray-400 m-0 mb-1'}>{s.label}</p>
            <p className={`font-bold text-lg m-0 ${s.color}`}>{s.value}</p>
          </div>
        ))}
      </div>

      {/* bill type breakdown + pending bills */}
      <div className={'grid grid-cols-1 md:grid-cols-3 gap-3 mb-4'}>
        <div className={'bg-cyan-50 border border-cyan-200 rounded-xl px-4 py-3 text-center'}>
          <p className={'text-xs text-gray-400 m-0 mb-1'}>GST Bills</p>
          <p className={'font-bold text-xl text-cyan-700 m-0'}>{summary.gstBills.length}</p>
          <p className={'text-xs text-cyan-600 m-0'}>
            {fmtCur(summary.gstBills.reduce((s, b) => s + (b.finalAmount ?? 0), 0))}
          </p>
        </div>
        <div className={'bg-orange-50 border border-orange-200 rounded-xl px-4 py-3 text-center'}>
          <p className={'text-xs text-gray-400 m-0 mb-1'}>Non-GST Bills</p>
          <p className={'font-bold text-xl text-orange-700 m-0'}>{summary.nonGstBills.length}</p>
          <p className={'text-xs text-orange-600 m-0'}>
            {fmtCur(summary.nonGstBills.reduce((s, b) => s + (b.finalAmount ?? 0), 0))}
          </p>
        </div>
        <div className={'bg-red-50 border border-red-200 rounded-xl px-4 py-3 text-center'}>
          <p className={'text-xs text-gray-400 m-0 mb-1'}>Pending / Partial Bills</p>
          <p className={'font-bold text-xl text-red-600 m-0'}>{summary.pending.length}</p>
          <p className={'text-xs text-red-500 m-0'}>{fmtCur(summary.totalPending)} outstanding</p>
        </div>
      </div>

      {/* products purchased */}
      {summary.products.length > 0 && (
        <>
          <Divider className={'my-3'} />
          <p className={'text-xs font-semibold text-gray-500 uppercase tracking-widest mb-3'}>
            <ShoppingOutlined className={'mr-1'} /> Products Purchased
          </p>
          <div className={'flex flex-wrap gap-2'}>
            {summary.products.map(p => (
              <div
                key={p.productName}
                className={'flex items-center gap-2 bg-primary-50 border border-primary-200 rounded-xl px-3 py-2'}
              >
                <span className={'font-semibold text-gray-800 text-sm'}>{p.productName}</span>
                <span className={'text-xs text-primary-700 font-bold bg-white border border-primary-100 rounded-lg px-2 py-0.5'}>
                  {p.totalQty} {p.unit || 'units'}
                </span>
                {p.totalBags > 0 && (
                  <span className={'text-xs text-orange-600 font-medium'}>({p.totalBags} bags)</span>
                )}
              </div>
            ))}
          </div>
        </>
      )}
    </Card>
  );
};
CustomerSummaryPanel.propTypes = {
  bills: PropTypes.array,
  customerName: PropTypes.string,
};

/* ── deleted bills modal ─────────────────────────────────────────────────── */
const DeletedBillsModal = ({open, onClose, token, onRestoreSuccess}) => {
  const {data: rawDeletedBills, isLoading, refetch} = RqGetDeletedBills(token, {enabled: open});

  const restoreBillMutation = RqRestoreBill({
    onSuccess: () => {
      message.success('Bill restored successfully');
      refetch();
      onRestoreSuccess?.();
    },
    onError: err => message.error(err?.response?.data?.message || err?.message || 'Restore failed'),
  });

  const permanentDeleteBillMutation = RqPermanentDeleteBill({
    onSuccess: () => {
      message.success('Bill permanently deleted');
      refetch();
      onRestoreSuccess?.();
    },
    onError: err => message.error(err?.response?.data?.message || err?.message || 'Permanent delete failed'),
  });

  const deletedBills = useMemo(() => {
    const list = rawDeletedBills?.data ?? rawDeletedBills ?? [];
    const thirtyDaysAgo = Date.now() - 30 * 24 * 60 * 60 * 1000;
    return list.filter(b => {
      const timestamp = b.deletedAt ? new Date(b.deletedAt).getTime() : (b.updatedAt ? new Date(b.updatedAt).getTime() : Date.now());
      return timestamp >= thirtyDaysAgo;
    });
  }, [rawDeletedBills]);

  const columns = [
    {
      title: 'Bill',
      dataIndex: 'billNumber',
      key: 'billNumber',
      render: (_, r) => (
        <Text strong className={'font-mono text-indigo-700'}>
          {formatBillNumber(r)}
        </Text>
      ),
    },
    {
      title: 'Customer',
      dataIndex: 'customerName',
      key: 'customerName',
      render: (v, r) => (
        <div>
          <Text strong>{v || '—'}</Text>
          <div className={'text-gray-400 text-xs'}>{r.customerMobile}</div>
        </div>
      ),
    },
    {
      title: 'Amount',
      dataIndex: 'finalAmount',
      key: 'finalAmount',
      render: v => <Text strong>{fmtCur(v)}</Text>,
    },
    {
      title: 'Deleted Date',
      key: 'date',
      render: (_, r) => fmt(r.deletedAt || r.updatedAt || r.createdAt),
    },
    {
      title: 'Action',
      key: 'action',
      align: 'center',
      render: (_, record) => (
        <Space size={'small'}>
          <Popconfirm
            title={'Restore this bill?'}
            description={'This action will restore the bill back to active bills.'}
            onConfirm={() => restoreBillMutation.mutate({id: record.id, token})}
            okText={'Yes, Restore'}
            cancelText={'Cancel'}
          >
            <Button
              size={'small'}
              type={'primary'}
              icon={<UndoOutlined />}
              loading={restoreBillMutation.isPending}
              className={'btn-primary'}
            >
              Restore
            </Button>
          </Popconfirm>

          <Popconfirm
            title={'Permanently delete this bill?'}
            description={'This action cannot be undone and will physically remove the record.'}
            onConfirm={() => permanentDeleteBillMutation.mutate({id: record.id, token})}
            okButtonProps={{danger: true}}
            okText={'Delete Permanently'}
            cancelText={'Cancel'}
          >
            <Button
              size={'small'}
              danger
              icon={<DeleteOutlined />}
              loading={permanentDeleteBillMutation.isPending}
            >
              Delete Permanently
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <Modal
      open={open}
      onCancel={onClose}
      destroyOnHidden
      width={800}
      title={(
        <div className={'flex items-center gap-2'}>
          <DeleteOutlined className={'text-red-500'} />
          <span className={'font-bold text-gray-800'}>Deleted Bills (Last 30 Days)</span>
        </div>
      )}
      footer={[
        <Button key={'close'} onClick={onClose}>Close</Button>,
      ]}
    >
      <Table
        columns={columns}
        dataSource={deletedBills.map(b => ({...b, key: b.id}))}
        loading={isLoading}
        pagination={{pageSize: 5}}
        size={'small'}
        locale={{
          emptyText: (
            <div className={'py-8 text-center text-gray-400'}>
              <DeleteOutlined className={'text-3xl mb-2 opacity-30'} />
              <div>No deleted bills found in the last 30 days</div>
            </div>
          ),
        }}
      />
    </Modal>
  );
};
DeletedBillsModal.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
  token: PropTypes.string,
  onRestoreSuccess: PropTypes.func,
};

/* ══════════════════════════════════════════════════════════════════════════════
   MAIN COMPONENT
══════════════════════════════════════════════════════════════════════════════ */
const BillingHistoryScreen = () => {
  const token = useSelector(state => state.auth.tokenData);

  const {data: rawBills, isLoading, isError, error, refetch} = RQGetAllBillsAPI(token);

  const deleteBillMutation = RqDeleteBill({
    onSuccess: () => { message.success('Bill deleted'); refetch(); },
    onError:   err => message.error(err?.message || 'Delete failed'),
  });

  /* ── local state ──────────────────────────────────────────────────────── */
  const [bills,            setBills]            = useState([]);
  const [editModal,        setEditModal]        = useState({open: false, bill: null});
  const [downloadModal,    setDownloadModal]    = useState({open: false, bill: null});
  const [deletedModalOpen, setDeletedModalOpen] = useState(false);
  const [currentPage,      setCurrentPage]      = useState(1);
  const [pageSize,         setPageSize]         = useState(10);
  const [customerSearch,   setCustomerSearch]   = useState('');
  const [billTypeFilter,   setBillTypeFilter]   = useState('');
  const [payStatusFilter,  setPayStatusFilter]  = useState('');
  const [billStatusFilter, setBillStatusFilter] = useState('');
  const [dateRange,        setDateRange]        = useState(null);

  const allBills = useMemo(() => rawBills?.data ?? rawBills ?? [], [rawBills]);

  useEffect(() => { setBills(allBills); }, [allBills]);

  /* ── date range filter ────────────────────────────────────────────────── */
  const handleDateFilter = useCallback(range => {
    setDateRange(range);
    setCurrentPage(1);
    if (!range?.[0] || !range?.[1]) { setBills(allBills); return; }
    const from = range[0].startOf('day').valueOf();
    const to   = range[1].endOf('day').valueOf();
    setBills(allBills.filter(b => {
      const t = new Date(b.createdAt).getTime();
      return t >= from && t <= to;
    }));
  }, [allBills]);

  /* ── derived display list ─────────────────────────────────────────────── */
  const displayBills = useMemo(() => {
    let r = [...bills].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
    if (customerSearch.trim()) {
      const q = customerSearch.toLowerCase();
      r = r.filter(b =>
        b.customerName?.toLowerCase().includes(q)   ||
        b.customerMobile?.includes(customerSearch)  ||
        b.businessName?.toLowerCase().includes(q),
      );
    }
    if (billTypeFilter)   {r = r.filter(b => b.billType      === billTypeFilter);}
    if (payStatusFilter)  {r = r.filter(b => b.paymentStatus === payStatusFilter);}
    if (billStatusFilter) {r = r.filter(b => b.billStatus    === billStatusFilter);}
    return r;
  }, [bills, customerSearch, billTypeFilter, payStatusFilter, billStatusFilter]);

  /* customer name inferred from search (if exactly matching) */
  const activeCustomerName = useMemo(() => {
    if (!customerSearch.trim()) {return null;}
    return displayBills[0]?.customerName ?? customerSearch;
  }, [customerSearch, displayBills]);

  /* ── overall stats (from allBills) ───────────────────────────────────── */
  const stats = useMemo(() => {
    const totalRevenue  = allBills.reduce((s, b) => s + (b.finalAmount   ?? 0), 0);
    const totalPending  = allBills.reduce((s, b) => s + (b.pendingAmount  ?? 0), 0);
    const pendingBills  = allBills.filter(b => b.paymentStatus === 'PENDING' || b.paymentStatus === 'PARTIAL');
    return {
      total: allBills.length,
      completed: allBills.filter(b => b.billStatus === 'COMPLETED').length,
      totalRevenue: +totalRevenue.toFixed(2),
      totalPending: +totalPending.toFixed(2),
      pendingCount: pendingBills.length,
    };
  }, [allBills]);

  const activeFilterCount = useMemo(() =>
    [dateRange, customerSearch, billTypeFilter, payStatusFilter, billStatusFilter].filter(Boolean).length,
  [dateRange, customerSearch, billTypeFilter, payStatusFilter, billStatusFilter]);

  const handleReset = () => {
    setCustomerSearch('');
    setBillTypeFilter('');
    setPayStatusFilter('');
    setBillStatusFilter('');
    setDateRange(null);
    setBills(allBills);
    setCurrentPage(1);
  };

  const paginatedBills = useMemo(() =>
    displayBills.slice((currentPage - 1) * pageSize, currentPage * pageSize),
  [displayBills, currentPage, pageSize]);

  /* ── unique customer names for autocomplete ───────────────────────────── */
  const customerOptions = useMemo(() => {
    const names = [...new Set(allBills.map(b => b.customerName).filter(Boolean))];
    return names.map(n => ({value: n, label: n}));
  }, [allBills]);

  /* ── table columns ────────────────────────────────────────────────────── */
  const mainColumns = useMemo(() => [
    {
      title: <span className={'flex items-center'}> Sr No.</span>, render: (_, __, index) => index + 1,
    },
    {
      title: <span className={'flex items-center gap-1'}><FileTextOutlined /> Bill</span>,
      dataIndex: 'billNumber',
      key: 'billNumber',
      render: (_, r) => (
        <div>
          <Text strong className={'font-mono text-indigo-700 text-sm'}>
            {formatBillNumber(r)}
          </Text>
          <div className={'mt-0.5'}>
            <Tag color={billTypeColor[r.billType] ?? 'default'} className={'text-xs'}>
              {r.billType?.replace('_', ' ')}
            </Tag>
          </div>
          <div className={'text-gray-400 text-xs mt-0.5'}>
            <CalendarOutlined className={'mr-1'} />{fmt(r.createdAt)}
          </div>
        </div>
      ),
    },
    {
      title: <span className={'flex items-center gap-1'}><UserOutlined /> Customer</span>,
      dataIndex: 'customerName', key: 'customerName',
      render: (v, r) => (
        <div>
          <Text strong>{v}</Text>
          <div className={'text-gray-500 text-xs mt-0.5'}>{r.customerMobile}</div>
          {r.businessName && r.businessName !== 'string' && (
            <div className={'text-gray-400 text-xs'}>{r.businessName}</div>
          )}
          {r.gstIn && <Tag color={'cyan'} className={'text-xs mt-0.5'}>GSTIN: {r.gstIn}</Tag>}
        </div>
      ),
    },
    {
      title: 'Items', dataIndex: 'items', key: 'items', align: 'center',
      render: v => <Badge count={v?.length ?? 0} showZero style={{backgroundColor: '#66798b'}} />,
    },
    {
      title: 'Amount', key: 'amount',
      sorter: (a, b) => Number(a.finalAmount ?? 0) - Number(b.finalAmount ?? 0),
      render: (_, r) => <AmountCell bill={r} />,
    },
    {
      title: 'Payment', key: 'payment', align: 'center',
      render: (_, r) => (
        <div className={'flex flex-col items-center gap-1'}>
          <Tag color={paymentStatusColor[r.paymentStatus] ?? 'default'}>{r.paymentStatus}</Tag>
          <Text type={'secondary'} className={'text-xs'}>{r.paymentMethod}</Text>
        </div>
      ),
    },
    {
      title: 'Bill Status', dataIndex: 'billStatus', key: 'billStatus', align: 'center',
      render: v => <Tag color={billStatusColor[v] ?? 'default'}>{v}</Tag>,
    },
    {
      title: 'Actions', key: 'actions', align: 'center',
      render: (_, record) => (
        <Space size={6}>
          <Tooltip title={'Download invoice'}>
            <Button
              size={'small'} icon={<DownloadOutlined />}
              className={'rounded-lg border-green-300 text-green-600'}
              onClick={e => { e.stopPropagation(); setDownloadModal({open: true, bill: record}); }}
            />
          </Tooltip>
          <Tooltip title={'Edit bill'}>
            <Button
              size={'small'} icon={<EditOutlined />}
              className={'rounded-lg border-blue-300 text-blue-600'}
              onClick={e => { e.stopPropagation(); setEditModal({open: true, bill: record}); }}
            />
          </Tooltip>
          <Tooltip title={'Delete bill'}>
            <Popconfirm
              title={'Delete this bill?'}
              description={'This action cannot be undone.'}
              onConfirm={() => deleteBillMutation.mutate({id: record.id, token})}
              okText={'Yes, Delete'} okButtonProps={{danger: true}}
              cancelText={'Cancel'}
            >
              <Button
                size={'small'} danger icon={<DeleteOutlined />}
                className={'rounded-lg'}
                onClick={e => e.stopPropagation()}
              />
            </Popconfirm>
          </Tooltip>
        </Space>
      ),
    },
  ], [token, deleteBillMutation]);

  const expandable = {
    expandedRowRender: bill => (
      <div className={'p-3 bg-blue-50/50 rounded-lg'}>
        <Table
          columns={itemCols}
          dataSource={(bill.items || []).map((it, i) => ({...it, key: i}))}
          pagination={false}
          size={'small'}
          bordered
          scroll={{x: 'max-content'}}
          className={'rounded-xl overflow-hidden'}
        />
        {/* bill summary strip */}
        <div className={'mt-3 flex flex-wrap gap-4 px-4 py-3 bg-white rounded-xl border border-blue-100 text-sm'}>
          <span><WalletOutlined className={'text-blue-400 mr-1'} />Final: <b>{fmtCur(bill.finalAmount)}</b></span>
          {bill.paidAmount > 0    && <span className={'text-emerald-700'}>Paid: <b>{fmtCur(bill.paidAmount)}</b></span>}
          {bill.pendingAmount > 0 && <span className={'text-red-600'}>Pending: <b>{fmtCur(bill.pendingAmount)}</b></span>}
          {bill.taxAmount > 0     && <span className={'text-orange-500'}>Tax: <b>{fmtCur(bill.taxAmount)}</b></span>}
          {bill.discountAmount > 0 && <span className={'text-green-600'}>Discount: <b>−{fmtCur(bill.discountAmount)}</b></span>}
          {bill.notes && bill.notes !== 'string' && <span className={'text-slate-500 italic'}>📝 {bill.notes}</span>}
        </div>
      </div>
    ),
    rowExpandable: record => (record.items?.length ?? 0) > 0,
  };

  /* ── render ───────────────────────────────────────────────────────────── */
  if (isLoading) {
    return (
      <div className={'flex justify-center items-center h-64'}>
        <Spin size={'large'} description={'Loading bills…'} />
      </div>
    );
  }

  return (
    <div className={'min-h-screen md:p-5 p-1'}>

      {/* header */}
      <div className={'flex items-center justify-between mb-6 flex-wrap gap-3'}>
        <div className={'flex items-center gap-3'}>
          <div
            className={'w-12 h-10 rounded-xl flex items-center justify-center text-white'}
            style={{background: 'linear-gradient(135deg, #3a5f98, #a7b8d1)'}}
          >
            <FileTextOutlined style={{fontSize: 18}} />
          </div>
          <Title level={3} className={'header !mb-0'}>Billing History</Title>
        </div>
        <div className={'flex items-center gap-3'}>
          <Button
            icon={<DeleteOutlined className={'text-red-500'} />}
            onClick={() => setDeletedModalOpen(true)}
            className={'rounded-xl border-red-200 text-slate-700 font-medium'}
          >
            Deleted Bills
          </Button>
          <Text type={'secondary'} className={'font-medium'}>
            {allBills.length} bills total
            {activeFilterCount > 0 && (
              <Tag color={'gold'} className={'ml-2 rounded-full'}>Filtered: {displayBills.length}</Tag>
            )}
          </Text>
        </div>
      </div>

      {/* overall stats */}
      <div className={'grid grid-cols-2 md:grid-cols-4 gap-3 mb-6'}>
        <StatCard label={'Total Bills'}    value={stats.total}                   icon={<FileTextOutlined className={'bg-color'} />} bg={'#e6e9ec'} />
        <StatCard label={'Completed'}      value={stats.completed}               icon={<CheckCircleFilled className={'bg-color'} />} bg={'#e6e9ec'} />
        <StatCard label={'Total Revenue'}  value={fmtCur(stats.totalRevenue)}    icon={<WalletOutlined className={'bg-color'} />}   bg={'#e6e9ec'} />
        <StatCard label={'Total Pending'}  value={fmtCur(stats.totalPending)}    icon={<CloseCircleFilled className={'bg-color'} />} bg={'#e6e9ec'} />
      </div>

      {/* ── Filters ── */}
      <Card className={'rounded-2xl border-0 shadow-sm mb-4 overflow-hidden'}>
        <div className={'flex items-center justify-between mb-3 flex-wrap gap-2'}>
          <div className={'flex items-center gap-2'}>
            <FilterOutlined className={'text-indigo-500'} />
            <span className={'font-semibold text-gray-700 text-sm'}>Filters</span>
            {activeFilterCount > 0 && (
              <Badge count={activeFilterCount} style={{backgroundColor: '#4f46e5'}} className={'ml-1'} />
            )}
          </div>
          <Text type={'secondary'} className={'text-xs shrink-0'}>
            Showing <b>{paginatedBills.length}</b> of <b>{displayBills.length}</b> bills
          </Text>
        </div>

        <Row gutter={[8, 8]} align={'middle'}>
          {/* Customer search */}
          <Col xs={24} sm={12} md={6}>
            <Select
              showSearch
              allowClear
              placeholder={'Search customer name / mobile…'}
              value={customerSearch || undefined}
              onChange={v => { setCustomerSearch(v ?? ''); setCurrentPage(1); }}
              onSearch={v => setCustomerSearch(v)}
              filterOption={(input, option) =>
                option?.value?.toLowerCase().includes(input.toLowerCase())
              }
              options={customerOptions}
              className={'w-full'}
              suffixIcon={<SearchOutlined className={'text-gray-400'} />}
              notFoundContent={(
                <Input
                  placeholder={'Type mobile number…'}
                  value={customerSearch}
                  onChange={e => { setCustomerSearch(e.target.value); setCurrentPage(1); }}
                  size={'small'}
                />
              )}
            />
          </Col>

          {/* Date range */}
          <Col xs={24} sm={24} md={7}>
            <RangePicker
              value={dateRange}
              onChange={handleDateFilter}
              placeholder={['From date', 'To date']}
              format={'DD MMM YYYY'}
              className={'w-full rounded-md'}
            />
          </Col>

          {/* Bill type */}
          <Col xs={12} sm={8} md={3}>
            <Select
              placeholder={'Bill Type'} allowClear className={'w-full'}
              value={billTypeFilter || undefined}
              onChange={v => { setBillTypeFilter(v ?? ''); setCurrentPage(1); }}
            >
              <Option value={'GST'}><Tag color={'cyan'}>GST</Tag></Option>
              <Option value={'NON_GST'}><Tag color={'geekblue'}>Non GST</Tag></Option>
            </Select>
          </Col>

          {/* Payment status */}
          <Col xs={12} sm={8} md={3}>
            <Select
              placeholder={'Pay Status'} allowClear className={'w-full'}
              value={payStatusFilter || undefined}
              onChange={v => { setPayStatusFilter(v ?? ''); setCurrentPage(1); }}
            >
              {['PAID', 'PENDING', 'PARTIAL', 'FAILED'].map(s => (
                <Option key={s} value={s}><Tag color={paymentStatusColor[s]}>{s}</Tag></Option>
              ))}
            </Select>
          </Col>

          {/* Bill status */}
          <Col xs={12} sm={8} md={3}>
            <Select
              placeholder={'Bill Status'} allowClear className={'w-full'}
              value={billStatusFilter || undefined}
              onChange={v => { setBillStatusFilter(v ?? ''); setCurrentPage(1); }}
            >
              {['COMPLETED', 'CANCELLED', 'HOLD', 'REFUNDED'].map(s => (
                <Option key={s} value={s}><Tag color={billStatusColor[s]}>{s}</Tag></Option>
              ))}
            </Select>
          </Col>

          {/* Reset */}
          <Col xs={12} sm={8} md={2}>
            <Button
              icon={<SyncOutlined />} onClick={handleReset}
              className={'rounded-md border-gray-300 text-gray-600 w-full'}
            >
              Reset
            </Button>
          </Col>
        </Row>
      </Card>

      {/* customer summary panel — only shown when customer filter active */}
      {customerSearch.trim() && (
        <CustomerSummaryPanel
          bills={displayBills}
          customerName={activeCustomerName}
        />
      )}

      {/* ── Table ── */}
      {isError ? (
        <Card className={'rounded-2xl shadow-sm'}>
          <div className={'py-16 text-center'}>
            <CloseCircleFilled className={'text-red-400 text-5xl mb-3'} />
            <div className={'font-semibold text-red-700 text-base'}>Failed to load bills</div>
            <div className={'text-gray-400 text-sm mt-1 mb-4'}>{error?.message}</div>
            <Button type={'primary'} danger onClick={refetch} icon={<ReloadOutlined />}>
              Try Again
            </Button>
          </div>
        </Card>
      ) : (
        <Table
          columns={mainColumns}
          dataSource={paginatedBills.map(b => ({...b, key: b.id}))}
          loading={isLoading}
          expandable={{...expandable, expandIconColumnIndex: mainColumns.length, 
            expandIcon: ({expanded, onExpand, record}) => expanded ? (
              <DownOutlined className={'text-gray-500 hover:text-blue-600 transition-transform'} onClick={e => onExpand(record, e)} />
            ) : (
              <RightOutlined className={'text-gray-500 hover:text-blue-600 transition-transform'} onClick={e => onExpand(record, e)} />
            )}}
          pagination={false}
          rowClassName={(_, i) =>
            `cursor-pointer transition-colors ${i % 2 === 0 ? 'bg-white' : 'bg-slate-50'} hover:!bg-blue-50`}
          locale={{
            emptyText: (
              <div className={'py-16 text-center text-gray-400'}>
                <FileTextOutlined className={'text-5xl mb-3 opacity-30'} />
                <div className={'font-semibold text-base'}>No bills found</div>
                <div className={'text-sm mt-1'}>Try adjusting your search or filters</div>
              </div>
            ),
          }}
          size={'middle'}
          scroll={{x: 'max-content'}}
        />
      )}

      {/* pagination */}
      <div className={'flex justify-between items-center px-4 py-3 border-t border-gray-100'}>
        <Select
          value={String(pageSize)}
          onChange={v => { setPageSize(Number(v)); setCurrentPage(1); }}
          style={{width: 120}}
        >
          {['10', '20', '30', '50'].map(s => (
            <Option key={s} value={s}>{s} / page</Option>
          ))}
        </Select>
        <Pagination
          current={currentPage}
          pageSize={pageSize}
          total={displayBills.length}
          showSizeChanger={false}
          onChange={page => setCurrentPage(page)}
        />
      </div>

      {/* Edit modal */}
      <EditBill
        open={editModal.open}
        bill={editModal.bill}
        token={token}
        onClose={() => setEditModal({open: false, bill: null})}
        onSuccess={refetch}
      />

      {/* Download modal */}
      <BillDownload
        open={downloadModal.open}
        bill={downloadModal.bill}
        onClose={() => setDownloadModal({open: false, bill: null})}
      />

      {/* Deleted Bills modal */}
      <DeletedBillsModal
        open={deletedModalOpen}
        onClose={() => setDeletedModalOpen(false)}
        token={token}
        onRestoreSuccess={refetch}
      />
    </div>
  );
};

export default BillingHistoryScreen;
